import { Alert, Badge } from "react-bootstrap";
import { useContext } from "react";
import { RebalanceContext } from "../../RebalanceInfo";
import { useAudit } from "../hooks/useAudit";
import type {
  AuditFinding,
  CheckpointKey,
  FindingSeverity,
} from "../lib/explainTypes";

interface Props {
  // The panel this block renders on. Only audit findings tagged with this
  // checkpoint render here (D8 / R10: each finding lands on its own panel).
  checkpoint: CheckpointKey;
}

// Bootstrap alert variant per severity, mirroring ChecksPanel's mapping
// (critical/error → danger, warning → warning, info → info).
const severityVariant = (severity: FindingSeverity): string => {
  switch (severity) {
    case "critical":
    case "error":
      return "danger";
    case "warning":
      return "warning";
    case "info":
      return "info";
    default:
      return "secondary";
  }
};

// One inline card per audit finding, reusing the `RedistributionWarningCard`
// idiom (a bootstrap `Alert` with a heading, body, and entity context). Renders
// the finding text, an entity reference (account / symbol / asset class), and —
// for a `spec_divergence` finding — the divergence assessment (AC4 third
// clause / AC5).
function AuditFindingCard({
  finding,
  resolveAccountName,
}: {
  finding: AuditFinding;
  resolveAccountName: (accountId: number) => string;
}) {
  const variant = severityVariant(finding.severity);
  const entity =
    finding.account_id != null
      ? resolveAccountName(finding.account_id)
      : (finding.symbol ?? finding.asset_class ?? null);

  return (
    <Alert variant={variant} className="mb-2" role="note">
      <Alert.Heading as="h6" className="d-flex align-items-center gap-2">
        <Badge bg={variant}>{finding.severity.toUpperCase()}</Badge>
        <span>{finding.title}</span>
        <Badge bg="secondary" className="ms-auto">
          {finding.source}
        </Badge>
      </Alert.Heading>
      <p className="mb-1">{finding.description}</p>
      {finding.recommendation && (
        <p className="mb-1">
          <strong>Recommendation:</strong> {finding.recommendation}
        </p>
      )}
      {finding.category === "spec_divergence" && finding.divergence_found && (
        <p className="mb-1">
          <Badge bg="dark" className="me-1">
            spec divergence
          </Badge>
          {finding.divergence_found.status}
          {finding.divergence_found.detail
            ? ` — ${finding.divergence_found.detail}`
            : ""}
        </p>
      )}
      <small className="text-muted">
        <code>{finding.code}</code>
        {entity && <span className="ms-2">{entity}</span>}
        {finding.agent && <span className="ms-2">{finding.agent}</span>}
      </small>
    </Alert>
  );
}

/**
 * Inline audit-findings block for a single Explain panel (D8 augmentation —
 * NOT a separate view or a fork of the tree). Reads the third primed cache
 * stream via {@link useAudit}, filters to the findings tagged with this
 * `checkpoint`, and renders one card per finding using the
 * `RedistributionWarningCard` idiom. Renders nothing when there are no findings
 * for this checkpoint so panels without audit signal are visually unchanged.
 */
export default function AuditFindingCards({ checkpoint }: Props) {
  const { rebalance, household } = useContext(RebalanceContext);
  const rebalanceId = rebalance?.id;
  const { data } = useAudit(rebalanceId);

  const audit = data?.data;
  const findings: AuditFinding[] = audit?.findings ?? [];
  const forCheckpoint = findings.filter((f) => f.checkpoint === checkpoint);
  if (forCheckpoint.length === 0) return null;

  const accountById = new Map(
    (household?.accounts ?? []).map((a) => [a.id, a]),
  );
  const resolveAccountName = (accountId: number): string => {
    const acct = accountById.get(accountId) as
      { label?: string; displayName?: string } | undefined;
    return acct?.label ?? acct?.displayName ?? `Account ${accountId}`;
  };

  return (
    <div aria-label={`Audit findings — ${checkpoint}`}>
      {forCheckpoint.map((finding, idx) => (
        <AuditFindingCard
          key={`${finding.code}-${idx}`}
          finding={finding}
          resolveAccountName={resolveAccountName}
        />
      ))}
    </div>
  );
}
